// Einstieg: Aufgaben laden, Routing über den URL-Hash (#<aufgaben-id>, leer = Übersicht).
// Die QR-Codes auf den Folien zeigen direkt auf https://<host>/#<aufgaben-id>.

import { api } from "./api.ts";
import type { CountInfo } from "./api.ts";
import { confetti } from "./confetti.ts";
import { Editor, gateSymbolSvg } from "./editor.ts";
import { ICON } from "./icons.ts";
import { badges, levels, newBadges } from "./progress.ts";
import type { Badge } from "./progress.ts";
import type { Circuit } from "./sim.ts";
import type { Task } from "./sim.ts";
import { store } from "./store.ts";

const app = document.getElementById("app")!;
let tasks: Task[] = [];
let editor: Editor | null = null;

const esc = (s: string) => s.replace(/[&<>"]/g, c => `&#${c.charCodeAt(0)};`);
const starStr = (n: number) => "★".repeat(n) + "☆".repeat(3 - n);

function overview(): void {
  const stars = store.stars();
  const total = tasks.reduce((sum, t) => sum + (stars[t.id] ?? 0), 0);
  const max = tasks.length * 3;
  const state = levels(tasks, stars);
  const shelf = badges(tasks, stars);
  const earnedCount = shelf.filter(b => b.earned).length;

  const card = (t: Task, i: number) => {
    const st = state[i];
    const inner = `
      <span class="num">${st === "done" ? ICON.check : i + 1}</span>
      <span class="title">${esc(t.titel)}
        ${st === "next" ? `<span class="tag">Vorschlag</span>` : ""}
        ${t.exkurs ? `<span class="tag exkurs">Exkurs</span>` : ""}</span>
      <span class="parts">${Object.keys(t.bauteile).map(gateSymbolSvg).join("")}</span>
      <span class="stars" aria-label="${stars[t.id] ?? 0} von 3 Sternen">${starStr(stars[t.id] ?? 0)}</span>`;
    return `<li><a href="#${t.id}" class="task-card ${st}">${inner}</a></li>`;
  };

  app.innerHTML = `
    <section class="overview">
      <h1>GateLab</h1>
      <p class="lead">Bauen Sie Schaltungen aus Logikgattern -- in beliebiger Reihenfolge.</p>
      <div class="progress" role="img" aria-label="${total} von ${max} Sternen">
        <div class="bar"><div style="width:${max ? (100 * total) / max : 0}%"></div></div>
        <span>${total} / ${max} ★</span>
      </div>
      <ol class="task-list level-path">${tasks.map(card).join("")}</ol>
      <h2>Abzeichen <span class="muted">${earnedCount} von ${shelf.length}</span></h2>
      <ul class="badge-shelf">
        ${shelf.map(b => `<li class="badge ${b.earned ? "earned" : ""}">
          <span class="badge-icon">${b.icon}</span>
          <span class="badge-text"><strong>${esc(b.name)}</strong><small>${esc(b.beschreibung)}</small></span>
        </li>`).join("")}
      </ul>
      <p class="privacy">Ihr Fortschritt wird nur in diesem Browser gespeichert.
        <button type="button" class="link-btn" data-act="reset">Fortschritt löschen</button></p>
      <p class="reset-confirm" hidden>Alle Sterne, Abzeichen und gebauten Schaltungen in diesem Browser löschen?
        <button type="button" class="danger" data-act="reset-yes">Ja, löschen</button>
        <button type="button" data-act="reset-no">Abbrechen</button></p>
    </section>`;

  const confirmBox = app.querySelector<HTMLElement>(".reset-confirm")!;
  app.querySelector(".overview")!.addEventListener("click", e => {
    const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act;
    if (act === "reset") {
      confirmBox.hidden = false;
      confirmBox.querySelector<HTMLElement>("[data-act=reset-no]")!.focus();
    } else if (act === "reset-no") {
      confirmBox.hidden = true;
    } else if (act === "reset-yes") {
      store.clearProgress();
      overview();
    }
  });
}

/** Kurze Einblendung für neu erreichte Abzeichen. */
function toastBadges(list: Badge[]): void {
  list.forEach((b, k) => {
    const el = document.createElement("div");
    el.className = "toast";
    el.setAttribute("role", "status");
    el.innerHTML = `<span class="badge-icon">${b.icon}</span>
      <span><small>Neues Abzeichen</small><strong>${esc(b.name)}</strong></span>`;
    el.style.bottom = `${16 + k * 76}px`;
    document.body.appendChild(el);
    window.setTimeout(() => el.classList.add("leave"), 4200);
    window.setTimeout(() => el.remove(), 4800);
  });
}

app.addEventListener("solved", e => {
  const { before } = (e as CustomEvent<{ before: Record<string, number> }>).detail;
  confetti();
  toastBadges(newBadges(tasks, before, store.stars()));
});

function route(): void {
  editor?.destroy();
  editor = null;
  const id = decodeURIComponent(location.hash.slice(1));
  const task = tasks.find(t => t.id === id);
  if (task) {
    document.title = `${task.titel} · GateLab`;
    const i = tasks.indexOf(task);
    const next = tasks[i + 1] ? { id: tasks[i + 1].id, titel: tasks[i + 1].titel } : null;
    editor = new Editor(app, task, next);
    api.open(task.id).then(showBarometer);
  } else {
    document.title = "GateLab";
    overview();
  }
  window.scrollTo(0, 0);
}

/** „Hörsaal-Barometer“: nur während einer laufenden Vorlesungs-Runde sichtbar. */
function showBarometer(info: CountInfo | null): void {
  const el = app.querySelector<HTMLElement>(".barometer");
  if (!el || !info?.round_active || info.solved_count === null) return;
  const n = info.solved_count;
  el.textContent = n === 0
    ? "Noch niemand im Hörsaal hat diese Aufgabe gelöst -- seien Sie die erste Person!"
    : `Schon ${n} ${n === 1 ? "Person hat" : "Personen haben"} diese Aufgabe im Hörsaal gelöst.`;
  el.hidden = false;
}

app.addEventListener("checked", e => {
  const { circuit, hints } = (e as CustomEvent<{ circuit: Circuit; hints: number }>).detail;
  const id = decodeURIComponent(location.hash.slice(1));
  api.solve(id, circuit, hints).then(showBarometer);
});

async function main(): Promise<void> {
  try {
    const res = await fetch("tasks.json", { cache: "no-cache" });
    tasks = (await res.json()).tasks;
  } catch {
    app.innerHTML = `<p class="load-error">Die Aufgaben konnten nicht geladen werden.</p>`;
    return;
  }
  window.addEventListener("hashchange", route);
  route();
}

main();
